import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { type RouteComponentProps, withRouter } from "react-router-dom";
import type { StaticContext } from "react-router";
import { type Dispatch, type UnknownAction } from "redux";
import { v4 as uuidv4 } from "uuid";

import {
  addTrader,
  addTraderReset,
  traderDetails,
  traderDetailsReset,
  updateTrader,
  updateTraderReset,
  listTraderRepresentatives,
  listTraderRepresentativesReset,
  addTraderRepresentative,
  addTraderRepresentativeReset,
  updateTraderRepresentative,
  updateTraderRepresentativeReset,
  deleteTraderRepresentative,
  deleteTraderRepresentativeReset,
  traderRepresentativeDetails,
  traderRepresentativeDetailsReset,
  listExchangeLocations,
  listExchangeLocationsReset,
  listExchangeLocationTypes,
  listExchangeLocationTypesReset,
  treeAdministrativeAreas,
  listAggregators,
  listAggregatorsReset,
} from "../../actions";
import type { AdministrativeAreaOption } from "../../components/AdministrativeAreaTreeSelect";
import { PageBreadcrumb } from "../../components/PageBreadcrumb";
import { GENDERS } from "../../constants";
import {
  getCountryObjectFromCache,
  getStatusErrorMessage,
  isEmpty,
  valueOrDefault,
} from "../../utils";
import {
  adaptAdministrativeAreas,
  adaptCardTypes,
  adaptLanguageForInitialValues,
  adaptLocationForInitialValues,
  adaptPhoneNumber,
  adaptTraderRepresentatives,
  removeCountryCode,
} from "../../utils/adapters";
import type {
  ExchangeLocation,
  ExchangeLocationAssignment,
  ExchangeLocationType,
} from "./components/ExchangeLocationForm/types";
import Form from "./components/Form";
import type {
  EntityId,
  TraderDetailsFormValues,
  TraderRepresentative,
} from "./components/Form/Components/types";
import {
  adaptTraderTypeForInitialValues,
  TRADER_TYPES,
} from "./components/Form/Components/TraderDetailsStepOne/utils";
import type { ComboboxOption } from "../InfoHub/Components/Form/Components/MultiSelectCombobox";
import type { RepresentativeFormValues, SelectOption } from "./components/RepresentativeForm/types";
import { ShoppingBasket } from "lucide-react";

type AppDispatch = Dispatch<UnknownAction>;
type RouteParams = {
  id?: string;
};
type TraderDetailsLocationState = {
  fromStep?: number;
};
type TraderDetailsRouteProps = RouteComponentProps<
  RouteParams,
  StaticContext,
  TraderDetailsLocationState | undefined
>;

interface ApiResponse<TData extends Record<string, unknown> = { id: EntityId }> {
  data: TData;
}

type ApiResult<TData extends Record<string, unknown> = { id: EntityId }> =
  | ApiResponse<TData>
  | null
  | undefined;

type ApiPromise<TData extends Record<string, unknown> = { id: EntityId }> = Promise<
  ApiResult<TData>
>;

type SelectableValue = EntityId | SelectOption | AdministrativeAreaOption | null | undefined;

type TraderOrganizationFormValues = Record<string, unknown> & {
  name?: string | null;
  trader_type?: SelectableValue;
  wings_id?: string | null;
  is_wfp_vendor?: boolean | null;
  other_info?: string | null;
  contact_email?: string | null;
  phone?: string | null;
  legal_address?: string | null;
  physical_address?: string | null;
  district?: string | null;
  location?: SelectableValue;
  legal_status?: SelectableValue;
  language?: SelectableValue;
  year_of_establishment?: string | number | null;
  bank_account?: string | null;
  provided_services?: SelectOption[] | null;
  commodities?: SelectOption[] | null;
  available_exchange_locations?: ExchangeLocationAssignment[] | null;
  eligible_aggregators?: SelectOption[] | null;
};

interface TraderOrganizationPayload {
  name?: string;
  trader_type?: EntityId;
  wings_id?: string;
  is_wfp_vendor: boolean;
  other_info?: string;
  contact_email?: string;
  phone?: string;
  legal_address?: string;
  physical_address?: string;
  district?: string;
  location?: EntityId;
  legal_status?: EntityId;
  language?: EntityId;
  year_of_establishment?: string | number;
  bank_account?: string;
  provided_services: EntityId[];
  commodities: EntityId[];
  available_exchange_locations: {
    exchange_location: EntityId;
    types: EntityId[];
  }[];
  eligible_aggregator_ids?: EntityId[];
}

interface RepresentativePayload {
  person: {
    uuid: string;
    first_name?: string;
    last_name?: string;
    contact_phone?: string;
  };
  email?: string;
  organization: EntityId;
}

interface TraderOrganizationApiData extends Record<string, unknown> {
  id?: EntityId;
  trader_type?: EntityId | null;
  language?: EntityId | null;
  location?: EntityId | null;
  available_exchange_locations?: {
    exchange_location: EntityId;
    types: EntityId[];
  }[];
  eligible_aggregators?: { id: EntityId; name: string }[];
}

interface RepresentativePersonApiData extends Record<string, unknown> {
  uuid?: string;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  contact_phone?: string;
  location?: unknown;
  identification_card_number?: string;
  gender?: EntityId;
  day_of_birth?: string | Date;
}

interface RepresentativeApiData extends Record<string, unknown> {
  id?: EntityId;
  email?: string;
  person?: RepresentativePersonApiData;
  position?: string;
}

interface RequestState<TData> {
  isFetching: boolean;
  isLoading?: boolean;
  isResetting?: boolean;
  errorMessage?: string;
  error?: unknown;
  data: TData;
}

interface RootState {
  traderDetails: RequestState<TraderOrganizationApiData | undefined>;
  listTraderRepresentatives: RequestState<{ results?: TraderRepresentative[] }>;
  traderRepresentativeDetails: RequestState<RepresentativeApiData>;
  addTraderRepresentative: RequestState<Record<string, unknown>>;
  updateTraderRepresentative: RequestState<Record<string, unknown>>;
  deleteTraderRepresentative: RequestState<Record<string, unknown>>;
  addTrader: RequestState<Record<string, unknown>>;
  updateTrader: RequestState<Record<string, unknown>>;
  treeAdministrativeAreas: RequestState<{ administrative_areas?: unknown[] }>;
  listExchangeLocations: RequestState<{ results?: ExchangeLocation[] }>;
  listExchangeLocationTypes: RequestState<{ results?: ExchangeLocationType[] }>;
  listAggregators: RequestState<{ results?: { id: EntityId; name: string }[] }>;
}

interface StateProps {
  id: string;
  initialFormValues: TraderDetailsFormValues;
  isFetching: boolean;
  isSavingProfile: boolean;
  isFetchingTraderRepresentatives: boolean;
  isFetchingExchangeLocations: boolean;
  errorMessage: string;
  fieldErrors: Record<string, unknown>;
  traderRepresentatives: TraderRepresentative[];
  isFetchingRepresentative: boolean;
  errorMessageRepresentative: string;
  fieldErrorsRepresentative: Record<string, unknown>;
  initialFormValuesRepresentative: RepresentativeFormValues;
  isOnEditRepresentative: boolean;
  isFetchingDeleteRepresentative: boolean;
  errorMessageDeleteRepresentative: string;
  administrativeAreas: AdministrativeAreaOption[];
  exchangeLocations: ExchangeLocation[];
  exchangeLocationTypes: ExchangeLocationType[];
  aggregatorOptions: ComboboxOption[];
  isFetchingAggregators: boolean;
}

interface DispatchProps {
  addTrader: (data: TraderOrganizationPayload) => ApiPromise;
  addTraderReset: () => unknown;
  updateTrader: (id: EntityId, data: TraderOrganizationPayload) => ApiPromise;
  updateTraderReset: () => unknown;
  traderDetails: (id: EntityId) => unknown;
  traderDetailsReset: () => unknown;
  listTraderRepresentatives: (organization: EntityId) => unknown;
  listTraderRepresentativesReset: () => unknown;
  addTraderRepresentative: (data: RepresentativePayload) => ApiPromise;
  addTraderRepresentativeReset: () => unknown;
  updateTraderRepresentative: (id: EntityId, data: RepresentativePayload) => ApiPromise;
  updateTraderRepresentativeReset: () => unknown;
  deleteTraderRepresentative: (id: EntityId) => ApiPromise;
  traderRepresentativeDetails: (id: EntityId) => unknown;
  traderRepresentativeDetailsReset: () => unknown;
  deleteTraderRepresentativeReset: () => unknown;
  treeAdministrativeAreas: () => unknown;
  listExchangeLocations: () => unknown;
  listExchangeLocationsReset: () => unknown;
  listExchangeLocationTypes: () => unknown;
  listExchangeLocationTypesReset: () => unknown;
  listAggregators: () => unknown;
  listAggregatorsReset: () => unknown;
}

type TraderDetailsProps = StateProps & DispatchProps & TraderDetailsRouteProps;

const dispatchAction = <TResult,>(dispatch: AppDispatch, action: unknown): TResult =>
  dispatch(action as UnknownAction) as TResult;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const asRecord = (value: unknown): Record<string, unknown> => (isRecord(value) ? value : {});

const getResults = <TResult,>(data?: { results?: TResult[] } | null): TResult[] =>
  data?.results ?? [];

const getOptionValue = (value: SelectableValue): EntityId | undefined => {
  if (isRecord(value) && "value" in value) {
    return valueOrDefault(value.value, undefined) as EntityId | undefined;
  }

  return valueOrDefault(value, undefined) as EntityId | undefined;
};

const getOptionalString = (value: unknown): string | undefined =>
  valueOrDefault(value, undefined) as string | undefined;

const getOptionalYear = (value: unknown): string | number | undefined =>
  valueOrDefault(value, undefined) as string | number | undefined;

const toOptionValues = (options?: SelectOption[] | null): EntityId[] =>
  options?.map(option => option.value) ?? [];

const hasEntityId = (id: EntityId | null | undefined): id is EntityId =>
  id !== undefined && id !== null && id !== "";

// A function to adapt values entered in form to a format accepted by the server
export const adaptFromValues = (
  formValues: TraderOrganizationFormValues
): TraderOrganizationPayload => {
  const traderType = getOptionValue(formValues.trader_type);
  // Eligible aggregators only belong to a School buyer; the backend rejects the
  // key for any other type, so only send it when the type is School (an empty
  // array then clears the set).
  const eligibleAggregators =
    traderType === TRADER_TYPES.school
      ? { eligible_aggregator_ids: toOptionValues(formValues.eligible_aggregators) }
      : {};

  return {
    name: getOptionalString(formValues.name),
    trader_type: traderType,
    wings_id: getOptionalString(formValues.wings_id),
    is_wfp_vendor: typeof formValues.is_wfp_vendor === "boolean" ? formValues.is_wfp_vendor : false,
    other_info: getOptionalString(formValues.other_info),
    contact_email: getOptionalString(formValues.contact_email),
    phone: adaptPhoneNumber(formValues.phone) as string | undefined,
    legal_address: getOptionalString(formValues.legal_address),
    physical_address: getOptionalString(formValues.physical_address),
    district: getOptionalString(formValues.district),
    location: getOptionValue(formValues.location),
    legal_status: getOptionValue(formValues.legal_status),
    language: getOptionValue(formValues.language),
    year_of_establishment: getOptionalYear(formValues.year_of_establishment),
    bank_account: getOptionalString(formValues.bank_account),
    provided_services: toOptionValues(formValues.provided_services),
    commodities: toOptionValues(formValues.commodities),
    available_exchange_locations:
      formValues.available_exchange_locations?.map(location => ({
        exchange_location: location.id,
        types: location.list,
      })) ?? [],
    ...eligibleAggregators,
  };
};

export const mergeDefinedFormValues = <TValues extends Record<string, unknown>>(
  ...formValueSets: Array<Partial<TValues> | null | undefined>
): TValues =>
  formValueSets.reduce<Partial<TValues>>((mergedValues, formValues) => {
    Object.entries(formValues || {}).forEach(([fieldName, value]) => {
      if (value !== undefined) {
        mergedValues[fieldName as keyof TValues] = value as TValues[keyof TValues];
      }
    });

    return mergedValues;
  }, {}) as TValues;

// A function to adapt values entered in form to a format accepted by the server
export const adaptRepresentativeFromValues = (
  formValues: RepresentativeFormValues,
  organization: EntityId
): RepresentativePayload => ({
  person: {
    uuid: getOptionalString(formValues.uuid) ?? uuidv4(),
    first_name: getOptionalString(formValues.first_name),
    last_name: getOptionalString(formValues.last_name),
    contact_phone: adaptPhoneNumber(formValues.contact_phone) as string | undefined,
  },
  email: getOptionalString(formValues.email),
  organization,
});

// A function to adapt values received from server to a format compatible with the form
export const adaptInitialFormValues = (
  data: TraderOrganizationApiData | null | undefined,
  administrativeAreas: AdministrativeAreaOption[]
): TraderDetailsFormValues => {
  if (!data) {
    return {};
  }

  return {
    ...data,
    trader_type: adaptTraderTypeForInitialValues(data.trader_type),
    language:
      typeof data.language === "string" ? adaptLanguageForInitialValues(data.language) : undefined,
    location: adaptLocationForInitialValues(data, administrativeAreas),
    available_exchange_locations: data.available_exchange_locations
      ? data.available_exchange_locations.map(location => ({
          id: location.exchange_location,
          list: location.types,
        }))
      : [],
    eligible_aggregators:
      data.eligible_aggregators?.map(aggregator => ({
        value: aggregator.id,
        label: aggregator.name,
      })) ?? [],
  };
};

// A function to adapt values received from server to a format compatible with the form
export const adaptRepresentativeInitialFromValues = (
  data: RepresentativeApiData | null | undefined
): RepresentativeFormValues => {
  if (!data?.person) {
    return {};
  }

  const genderOption = data.person.gender
    ? adaptCardTypes(GENDERS).find((gender: SelectOption) => data.person?.gender === gender.value)
    : undefined;

  return {
    uuid: getOptionalString(data.person.uuid) ?? uuidv4(),
    first_name: getOptionalString(data.person.first_name),
    middle_name: getOptionalString(data.person.middle_name),
    last_name: getOptionalString(data.person.last_name),
    contact_phone: valueOrDefault(removeCountryCode(data.person.contact_phone), undefined),
    location: valueOrDefault(data.person.location, undefined),
    identification_card_number: getOptionalString(data.person.identification_card_number),
    gender: valueOrDefault(genderOption, undefined),
    day_of_birth: data.person.day_of_birth ? new Date(data.person.day_of_birth) : undefined,
    email: getOptionalString(data.email),
    position: getOptionalString(data.position),
  };
};

// A function to adapt field errors for Representative record to a flat object
export const adaptRepresentativeFieldErrors = (error: unknown): Record<string, unknown> => {
  if (isEmpty(error)) {
    return {};
  }

  const errorRecord = asRecord(error);
  const data = asRecord(errorRecord.data);

  if (!isEmpty(data)) {
    return {
      ...asRecord(data.person),
      ...data,
    };
  }

  return {
    ...asRecord(errorRecord.person),
    ...errorRecord,
  };
};

// This component is used to display the detail of a Trader
export const TraderDetails = ({
  isFetching,
  isSavingProfile,
  isFetchingTraderRepresentatives,
  errorMessage,
  fieldErrors,
  addTrader,
  addTraderReset,
  history,
  id,
  traderDetails,
  traderDetailsReset,
  updateTrader,
  updateTraderReset,
  initialFormValues,
  listTraderRepresentatives,
  listTraderRepresentativesReset,
  addTraderRepresentative,
  addTraderRepresentativeReset,
  updateTraderRepresentative,
  updateTraderRepresentativeReset,
  deleteTraderRepresentative,
  traderRepresentatives,
  isFetchingRepresentative,
  errorMessageRepresentative,
  traderRepresentativeDetails,
  traderRepresentativeDetailsReset,
  fieldErrorsRepresentative,
  initialFormValuesRepresentative,
  isOnEditRepresentative,
  isFetchingDeleteRepresentative,
  errorMessageDeleteRepresentative,
  deleteTraderRepresentativeReset,
  treeAdministrativeAreas,
  administrativeAreas,
  listExchangeLocations,
  listExchangeLocationsReset,
  isFetchingExchangeLocations,
  exchangeLocations,
  listExchangeLocationTypes,
  listExchangeLocationTypesReset,
  exchangeLocationTypes,
  listAggregators,
  listAggregatorsReset,
  aggregatorOptions,
  isFetchingAggregators,
}: TraderDetailsProps) => {
  const [inEditRepresentative, setInEditRepresentative] = useState<EntityId | undefined>();
  const [isDeleteRepresentativeModalOpen, setIsDeleteRepresentativeModalOpen] = useState(false);
  const [currentToDeleteRepresentative, setCurrentToDeleteRepresentative] =
    useState<TraderRepresentative | null>(null);
  const [hasCustomExchangeLocationFeatureEnabled] = useState(
    () => typeof localStorage !== "undefined" && localStorage.getItem("selected_country") === "BD"
  );
  const { t } = useTranslation();
  const initialStep = history.location.state?.fromStep;

  useEffect(() => {
    if (id) {
      traderDetails(id);
      listTraderRepresentatives(id);
    }
  }, [id, listTraderRepresentatives, traderDetails]);

  useEffect(() => {
    listExchangeLocations();
    listExchangeLocationTypes();
    treeAdministrativeAreas();
    listAggregators();

    return () => {
      addTraderReset();
      updateTraderReset();
      traderDetailsReset();
      listTraderRepresentativesReset();
      addTraderRepresentativeReset();
      updateTraderRepresentativeReset();
      traderRepresentativeDetailsReset();
      deleteTraderRepresentativeReset();
      listExchangeLocationsReset();
      listExchangeLocationTypesReset();
      listAggregatorsReset();
    };
  }, [
    addTraderRepresentativeReset,
    addTraderReset,
    deleteTraderRepresentativeReset,
    listAggregators,
    listAggregatorsReset,
    listExchangeLocationTypes,
    listExchangeLocationTypesReset,
    listExchangeLocations,
    listExchangeLocationsReset,
    listTraderRepresentativesReset,
    traderDetailsReset,
    traderRepresentativeDetailsReset,
    treeAdministrativeAreas,
    updateTraderRepresentativeReset,
    updateTraderReset,
  ]);

  const saveOrganization = (
    close: boolean,
    formValues: TraderDetailsFormValues,
    currentStep?: number
  ) => {
    const redirect = (savedTraderId: EntityId) => {
      if (close) {
        history.push("/wfpManager/traders");
        return;
      }

      history.push(`/wfpManager/traderDetails/${savedTraderId}`, {
        fromStep: currentStep ? currentStep + 1 : 1,
      });
    };

    const data = adaptFromValues(formValues as TraderOrganizationFormValues);
    const promise = id ? updateTrader(id, data) : addTrader(data);

    return promise.then(result => {
      const savedTraderId = result?.data.id;
      if (hasEntityId(savedTraderId)) {
        redirect(savedTraderId);
        if (id) {
          traderDetails(savedTraderId);
          listTraderRepresentatives(savedTraderId);
        }
      }

      return result;
    });
  };

  const onClickValidateAndClose = (formValues: TraderDetailsFormValues) =>
    saveOrganization(true, formValues);

  const onClickValidateAndContinue = (formValues: TraderDetailsFormValues, currentStep: number) =>
    saveOrganization(false, formValues, currentStep);

  const saveExchangeLocations = (exchangeLocationsForm: TraderDetailsFormValues) => {
    if (!id) {
      return Promise.resolve(undefined);
    }

    const data = adaptFromValues(
      mergeDefinedFormValues<TraderOrganizationFormValues>(
        initialFormValues as TraderOrganizationFormValues,
        exchangeLocationsForm as TraderOrganizationFormValues
      )
    );

    return updateTrader(id, data).then(result => {
      if (result) {
        history.push("/wfpManager/traders");
      }

      return result;
    });
  };

  const onClickBackRepresentativeForm = () => {
    setInEditRepresentative(undefined);
    traderRepresentativeDetailsReset();
  };

  const onClickValidateRepresentativeForm = (
    formValuesRepresentative: RepresentativeFormValues
  ) => {
    if (!id) {
      return Promise.resolve(undefined);
    }

    const reset = () => {
      listTraderRepresentatives(id);
      setInEditRepresentative(undefined);
      traderRepresentativeDetailsReset();
      history.push(`/wfpManager/traderDetails/${id}`, { fromStep: 1 });
    };
    const values = adaptRepresentativeFromValues(formValuesRepresentative, id);

    if (hasEntityId(inEditRepresentative)) {
      return updateTraderRepresentative(inEditRepresentative, values).then(result => {
        if (result) {
          reset();
        }

        return result;
      });
    }

    return addTraderRepresentative(values).then(result => {
      if (result) {
        reset();
      }

      return result;
    });
  };

  const onClickEditRepresentative = (record: TraderRepresentative) => {
    if (!hasEntityId(record.id)) {
      return;
    }

    setInEditRepresentative(record.id);
    traderRepresentativeDetails(record.id);
  };

  const onClickDeleteRepresentative = (record: TraderRepresentative) => {
    setIsDeleteRepresentativeModalOpen(true);
    setCurrentToDeleteRepresentative(record);
  };

  const onClickConfirmDeleteRepresentative = () => {
    const representativeId = currentToDeleteRepresentative?.id;
    if (!hasEntityId(representativeId)) {
      return Promise.resolve(undefined);
    }

    return deleteTraderRepresentative(representativeId).then(result => {
      if (result) {
        setIsDeleteRepresentativeModalOpen(false);
        setCurrentToDeleteRepresentative(null);
        listTraderRepresentatives(id);
        deleteTraderRepresentativeReset();
      }

      return result;
    });
  };

  const onClickCancelDeleteRepresentative = () => {
    setIsDeleteRepresentativeModalOpen(false);
    setCurrentToDeleteRepresentative(null);
    deleteTraderRepresentativeReset();
  };

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex flex-col gap-4">
        <PageBreadcrumb
          items={[
            { label: t("traders.title"), href: "/wfpManager/traders" },
            { label: t("traderDetails.title") },
          ]}
        />
        <div className="flex items-center gap-x-2">
          <span className="inline-flex shrink-0 items-center justify-center size-10 rounded-lg p-2 text-green-600 bg-green-100">
            <ShoppingBasket />
          </span>
          <h1 className="font-semibold text-2xl">{t("traderDetails.title")}</h1>
        </div>
      </div>
      <div className="w-full">
        <Form
          id={id}
          initialValues={initialFormValues}
          isFetching={isFetching}
          isSavingProfile={isSavingProfile}
          isFetchingTraderRepresentatives={isFetchingTraderRepresentatives}
          isFetchingExchangeLocations={isFetchingExchangeLocations}
          errorMessage={errorMessage}
          fieldErrors={fieldErrors}
          onClickValidateAndClose={onClickValidateAndClose}
          onClickValidateAndContinue={onClickValidateAndContinue}
          traderRepresentatives={traderRepresentatives}
          onClickEditRepresentative={onClickEditRepresentative}
          onClickDeleteRepresentative={onClickDeleteRepresentative}
          isDeleteRepresentativeModalOpen={isDeleteRepresentativeModalOpen}
          onClickConfirmDeleteRepresentative={onClickConfirmDeleteRepresentative}
          onClickCancelDeleteRepresentative={onClickCancelDeleteRepresentative}
          currentToDeleteRepresentative={currentToDeleteRepresentative}
          isFetchingDeleteRepresentative={isFetchingDeleteRepresentative}
          errorMessageDeleteRepresentative={errorMessageDeleteRepresentative}
          administrativeAreas={administrativeAreas}
          aggregatorOptions={aggregatorOptions}
          isFetchingAggregators={isFetchingAggregators}
          initialStep={initialStep}
          initialFormValuesRepresentative={initialFormValuesRepresentative}
          isFetchingRepresentative={isFetchingRepresentative}
          errorMessageRepresentative={errorMessageRepresentative}
          onClickValidateRepresentativeForm={onClickValidateRepresentativeForm}
          onClickValidateExchangeLocationsForm={saveExchangeLocations}
          fieldErrorsRepresentative={fieldErrorsRepresentative}
          onClickBackRepresentativeForm={onClickBackRepresentativeForm}
          isOnEditRepresentative={isOnEditRepresentative}
          exchangeLocations={exchangeLocations}
          exchangeLocationTypes={exchangeLocationTypes}
          hasCustomExchangeLocationFeatureEnabled={hasCustomExchangeLocationFeatureEnabled}
        />
      </div>
    </main>
  );
};

// Starting from the redux state it gets data related to logged in user
export const mapStateToProps = (state: RootState, props: TraderDetailsRouteProps): StateProps => {
  const id = props.match.params.id || "";
  const hasTraderDetails = !isEmpty(state.traderDetails.data);
  const hasJustSavedTrader = !isEmpty(state.addTrader.data) || !isEmpty(state.updateTrader.data);
  const isFetchingInitialDetails =
    !!id && state.traderDetails.isFetching && !hasTraderDetails && !hasJustSavedTrader;
  const administrativeAreasTree = adaptAdministrativeAreas(
    state.treeAdministrativeAreas.data.administrative_areas
  ) as AdministrativeAreaOption[];

  return {
    id,
    initialFormValues: adaptInitialFormValues(state.traderDetails.data, administrativeAreasTree),
    isFetching: isFetchingInitialDetails || state.treeAdministrativeAreas.isFetching,
    isSavingProfile: !!state.addTrader.isLoading || !!state.updateTrader.isLoading,
    isFetchingTraderRepresentatives: state.listTraderRepresentatives.isFetching,
    isFetchingExchangeLocations:
      state.listExchangeLocations.isFetching ||
      !!state.listExchangeLocations.isResetting ||
      state.listExchangeLocationTypes.isFetching ||
      !!state.listExchangeLocationTypes.isResetting,
    errorMessage:
      state.listTraderRepresentatives.errorMessage ||
      state.traderDetails.errorMessage ||
      getStatusErrorMessage(state.addTrader.error) ||
      getStatusErrorMessage(state.updateTrader.error) ||
      state.treeAdministrativeAreas.errorMessage ||
      "",
    fieldErrors:
      (!isEmpty(state.addTrader.error) && asRecord(state.addTrader.error)) ||
      (!isEmpty(state.updateTrader.error) && asRecord(state.updateTrader.error)) ||
      {},
    traderRepresentatives: adaptTraderRepresentatives(
      getResults(state.listTraderRepresentatives.data)
    ) as TraderRepresentative[],
    isFetchingRepresentative:
      !!state.addTraderRepresentative.isLoading ||
      !!state.updateTraderRepresentative.isLoading ||
      state.traderRepresentativeDetails.isFetching,
    errorMessageRepresentative:
      getStatusErrorMessage(state.addTraderRepresentative.error) ||
      getStatusErrorMessage(state.updateTraderRepresentative.error) ||
      "",
    fieldErrorsRepresentative:
      adaptRepresentativeFieldErrors(state.addTraderRepresentative.error) ||
      adaptRepresentativeFieldErrors(state.updateTraderRepresentative.error) ||
      {},
    initialFormValuesRepresentative: adaptRepresentativeInitialFromValues(
      state.traderRepresentativeDetails.data
    ),
    isOnEditRepresentative: state.traderRepresentativeDetails.data.id !== undefined,
    isFetchingDeleteRepresentative: !!state.deleteTraderRepresentative.isLoading,
    errorMessageDeleteRepresentative: state.deleteTraderRepresentative.errorMessage || "",
    administrativeAreas: administrativeAreasTree,
    exchangeLocations: getResults(state.listExchangeLocations.data),
    exchangeLocationTypes: getResults(state.listExchangeLocationTypes.data),
    aggregatorOptions: getResults(state.listAggregators.data).map(aggregator => ({
      value: aggregator.id,
      label: aggregator.name,
    })),
    isFetchingAggregators: state.listAggregators.isFetching,
  };
};

// Maps functions to dispatch actions
export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  const countryId = getCountryObjectFromCache().id as EntityId;

  return {
    addTrader: data => dispatchAction<ApiPromise>(dispatch, addTrader(data)),
    addTraderReset: () => dispatchAction(dispatch, addTraderReset()),
    updateTrader: (id, data) => dispatchAction<ApiPromise>(dispatch, updateTrader(id, data)),
    updateTraderReset: () => dispatchAction(dispatch, updateTraderReset()),
    traderDetails: id => dispatchAction(dispatch, traderDetails(String(id))),
    traderDetailsReset: () => dispatchAction(dispatch, traderDetailsReset()),
    listTraderRepresentatives: organization =>
      dispatchAction(dispatch, listTraderRepresentatives({ organization })),
    listTraderRepresentativesReset: () =>
      dispatchAction(dispatch, listTraderRepresentativesReset()),
    addTraderRepresentative: data =>
      dispatchAction<ApiPromise>(dispatch, addTraderRepresentative(data)),
    addTraderRepresentativeReset: () => dispatchAction(dispatch, addTraderRepresentativeReset()),
    updateTraderRepresentative: (id, data) =>
      dispatchAction<ApiPromise>(dispatch, updateTraderRepresentative(id, data)),
    updateTraderRepresentativeReset: () =>
      dispatchAction(dispatch, updateTraderRepresentativeReset()),
    deleteTraderRepresentative: id =>
      dispatchAction<ApiPromise>(dispatch, deleteTraderRepresentative(id)),
    traderRepresentativeDetails: id =>
      dispatchAction(dispatch, traderRepresentativeDetails(String(id))),
    traderRepresentativeDetailsReset: () =>
      dispatchAction(dispatch, traderRepresentativeDetailsReset()),
    deleteTraderRepresentativeReset: () =>
      dispatchAction(dispatch, deleteTraderRepresentativeReset()),
    treeAdministrativeAreas: () =>
      dispatchAction(dispatch, treeAdministrativeAreas({}, `${countryId}/administrative_areas/`)),
    listExchangeLocations: () => dispatchAction(dispatch, listExchangeLocations()),
    listExchangeLocationsReset: () => dispatchAction(dispatch, listExchangeLocationsReset()),
    listExchangeLocationTypes: () => dispatchAction(dispatch, listExchangeLocationTypes()),
    listExchangeLocationTypesReset: () =>
      dispatchAction(dispatch, listExchangeLocationTypesReset()),
    listAggregators: () => dispatchAction(dispatch, listAggregators({ page_size: 9999 })),
    listAggregatorsReset: () => dispatchAction(dispatch, listAggregatorsReset()),
  };
};

// The component uses the redux store
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(TraderDetails));
